/**
 * Store Selection Context
 * Manages currently selected store, user location, and persistence
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useToast } from './ToastContext';

const StoreContext = createContext(undefined);

export function StoreProvider({ children }) {
  const [selectedStore, setSelectedStore] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();

  /**
   * Restore selected store and user location from localStorage on mount
   */
  useEffect(() => {
    try {
      // Restore store
      const savedStore = localStorage.getItem('tcr_selected_store');
      if (savedStore && savedStore !== 'undefined' && savedStore !== 'null') {
        const parsedStore = JSON.parse(savedStore);
        if (parsedStore && typeof parsedStore === 'object') {
          setSelectedStore(parsedStore);
          console.log('🏪 Store restored:', parsedStore.name);
        }
      }

      // Restore user location
      const savedLocation = localStorage.getItem('tcr_user_location');
      if (savedLocation && savedLocation !== 'undefined' && savedLocation !== 'null') {
        const parsedLocation = JSON.parse(savedLocation);
        if (parsedLocation && parsedLocation.lat && parsedLocation.lng) {
          setUserLocation(parsedLocation);
          console.log('📍 Location restored:', parsedLocation);
        }
      }
    } catch (error) {
      console.error('❌ Failed to parse store/location data:', error);
      localStorage.removeItem('tcr_selected_store');
      localStorage.removeItem('tcr_user_location');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Select a store and save to localStorage
   */
  const selectStore = useCallback((store) => {
    if (!store || !store.id) {
      console.error('❌ Invalid store data:', store);
      return;
    }

    // Normalize store data structure
    const storeData = {
      id: store.id || store._id,
      name: store.name || store.storeName,
      address: store.address,
      distance: store.distance,
      coordinates: store.coordinates || {
        lat: store.location?.coordinates?.[1],
        lng: store.location?.coordinates?.[0],
      },
      status: store.status || 'open',
      isAcceptingOrders: store.isAcceptingOrders !== undefined ? store.isAcceptingOrders : true,
      minOrderAmount: store.minOrderAmount,
      openingTime: store.openingTime,
      closingTime: store.closingTime,
      contactNumber: store.contactNumber,

      // Persist company linkage so customer views can call company-scoped APIs (offers, etc.)
      companyId: store.companyId || store.company?._id || store.companyId?._id || null,
      storeCode: store.storeCode || store.storeCode || null,
    };

    // Check if same store is already selected - don't show toast
    const isSameStore = selectedStore?.id === storeData.id;

    setSelectedStore(storeData);
    localStorage.setItem('tcr_selected_store', JSON.stringify(storeData));

    console.log('✅ Store selected:', storeData.name, 'companyId=', storeData.companyId);
    
    // Only show toast if it's a different store
    if (!isSameStore) {
      showToast(`Store set to ${storeData.name}`, 'success');
    }
  }, [showToast, selectedStore]);

  /**
   * Clear selected store
   */
  const clearStore = useCallback(() => {
    setSelectedStore(null);
    localStorage.removeItem('tcr_selected_store');
    console.log('🗑️ Store cleared');
  }, []);

  /**
   * Save user location (called after geolocation permission granted)
   */
  const saveUserLocation = useCallback((coords) => {
    if (!coords || !coords.lat || !coords.lng) {
      console.error('❌ Invalid location coordinates:', coords);
      return;
    }

    const locationData = {
      lat: coords.lat,
      lng: coords.lng,
      timestamp: Date.now(),
    };

    setUserLocation(locationData);
    localStorage.setItem('tcr_user_location', JSON.stringify(locationData));
    console.log('📍 Location saved:', locationData);
  }, []);

  /**
   * Clear user location
   */
  const clearUserLocation = useCallback(() => {
    setUserLocation(null);
    localStorage.removeItem('tcr_user_location');
    console.log('🗑️ Location cleared');
  }, []);

  const [isRequestingLocation, setIsRequestingLocation] = useState(false);

  /**
   * Grant location permission and save user location
   */
  const requestLocation = useCallback((options = {}) => {
    return new Promise((resolve, reject) => {
      if (isRequestingLocation) {
        resolve(userLocation);
        return;
      }

      setIsRequestingLocation(true);

      if (!navigator.geolocation) {
        const err = new Error('Geolocation is not supported by your browser');
        setIsRequestingLocation(false);
        showToast(err.message, 'error');
        reject(err);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            timestamp: Date.now(),
          };
          saveUserLocation(locationData);
          setIsRequestingLocation(false);
          if (options.showToast !== false) {
            showToast('Location updated successfully!', 'success');
          }
          resolve(locationData);
        },
        (err) => {
          console.error('Geolocation error:', err);
          setIsRequestingLocation(false);
          let errorMessage = 'Unable to get your location.';
          if (err.code === 1) {
            errorMessage = 'Location permission denied. Please enable location in your browser settings.';
          } else if (err.code === 2) {
            errorMessage = 'Location unavailable. Please try again.';
          } else if (err.code === 3) {
            errorMessage = 'Location request timed out. Please try again.';
          }
          showToast(errorMessage, 'error');
          reject(err);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
          ...options
        }
      );
    });
  }, [isRequestingLocation, saveUserLocation, showToast, userLocation]);

  /**
   * Check if user has granted location permission
   */
  const hasLocation = !!userLocation;

  /**
   * Change store (clears cart warning could be added here)
   */
  const changeStore = useCallback((newStore) => {
    if (selectedStore?.id === newStore?.id) {
      console.log('ℹ️ Same store selected, no change needed');
      return;
    }

    // TODO: In future, add cart warning if cart has items
    // For now, just switch the store
    selectStore(newStore);
  }, [selectedStore, selectStore]);

  /**
   * Check if a specific store is currently selected
   */
  const isStoreSelected = useCallback((storeId) => {
    return selectedStore?.id === storeId;
  }, [selectedStore]);

  /**
   * Get store ID (handles both formats)
   */
  const getStoreId = useCallback(() => {
    return selectedStore?.id || selectedStore?._id || null;
  }, [selectedStore]);

  const value = {
    // State
    selectedStore,
    userLocation,
    isLoading,
    isRequestingLocation,

    // Methods
    selectStore,
    clearStore,
    changeStore,
    isStoreSelected,
    getStoreId,
    saveUserLocation,
    clearUserLocation,
    requestLocation,

    // Computed
    hasStore: !!selectedStore,
    hasLocation,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}

/**
 * Custom hook to use store context
 */
export function useStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}

export default StoreContext;
